import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NbToastrService } from '@nebular/theme';
import { AuthService, LOGOUT_PATH, SESSION_EXPIRED_MESSAGE } from '@services/auth.service';
import { isUnknownSortPropertyError } from '@/utils/sort-error';
import { userFacingMessage } from '@/utils/user-facing-message';

const GENERIC_BAD_REQUEST = 'Die Eingabe konnte nicht verarbeitet werden.';

/**
 * Only reasons the backend explicitly marked as user-facing (`UserBadRequestException`) reach the
 * toast. Most 400 reasons are developer English ("Creator tenant required", "Invalid sort parameter:
 * $sort") or machine codes ("CERTIFICATE_ALREADY_EXISTS") that would be meaningless — or alarming —
 * in a red toast, so everything unmarked falls back to the generic text.
 *
 * Deciding here rather than by endpoint keeps the two sides together: whoever writes a German
 * message opts it in at the throw site, instead of a URL list in the frontend going stale.
 */
const badRequestMessage = (body: unknown): string => userFacingMessage(body) ?? GENERIC_BAD_REQUEST;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly nbToastrService = inject(NbToastrService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError(err => {
        // Der Logout-Call meldet ohnehin ab; ein Toast oder ein weiterer logout() daraus
        // wäre nur Rauschen bzw. eine Endlosschleife.
        if (request.url.endsWith(LOGOUT_PATH)) {
          return throwError(() => err);
        }
        if ((err.status === 400 || err.status === 401) && err.url?.endsWith('/api/auth/login')) {
          this.nbToastrService.danger('Bitte Eingabe überprüfen und erneut versuchen.', 'Login inkorrekt');
        } else if (err.status === 401) {
          this.handleUnauthorized(request, err);
        } else if (err.status === 403) {
          if (err.error?.error === 'PASSWORD_CHANGE_REQUIRED') {
            this.authService.markPasswordChangeRequired();
            this.router.navigate(['/force-password-change']);
          } else {
            this.nbToastrService.danger('Diese Operation ist nicht erlaubt', 'Zugriff verweigert');
          }
        } else if (err.status === 400) {
          // Verschachtelt statt nebeneinander: zwei getrennte 400er-Zweige machen den jeweils
          // zweiten zu totem Code, ohne dass tsc oder ESLint etwas melden.
          if (isUnknownSortPropertyError(err)) {
            this.nbToastrService.warning('Die gewählte Sortierung wird nicht unterstützt und wurde entfernt', 'Sortierung');
          } else {
            this.nbToastrService.danger(badRequestMessage(err.error), 'Ungültige Eingabe');
          }
        } else if (err.status === 404) {
          this.nbToastrService.danger('Die angeforderte Ressource wurde nicht gefunden', 'Nicht gefunden');
        } else if (err.status === 409) {
          this.nbToastrService.danger('Neuere Version existiert bereits', 'Version stimmt nicht überein');
        } else if (err.status === 430) {
          this.nbToastrService.danger('Die App ist veraltet - bitte aktualisieren', 'App veraltet');
        } else if (err.status === 503) {
          this.nbToastrService.danger(err.error?.detail ?? 'Der Dienst ist derzeit nicht verfügbar', 'Nicht verfügbar');
        } else {
          this.nbToastrService.danger('Ein unbekannter Fehler ist aufgetreten. Bitte erneut versuchen!', 'Fehler');
        }
        const error = err.error || err.message || err.statusText;
        return throwError(error);
      }),
    );
  }

  private handleUnauthorized(request: HttpRequest<unknown>, err: HttpErrorResponse): void {
    if (this.authService.isQrSessionRequest(request.url)) {
      if (request.headers.has('Authorization')) {
        this.authService.discardCredentials();
      }
      return;
    }
    if (err.error?.message === 'TOKEN_EXPIRED') {
      this.nbToastrService.warning(SESSION_EXPIRED_MESSAGE, 'Sitzung abgelaufen');
    } else {
      this.nbToastrService.danger('Benutzer ist nicht eingeloggt', 'Fehler');
    }
    // Der Token ist serverseitig schon abgelehnt - ein Widerruf würde nur die
    // Sitzungen desselben Benutzers auf anderen Geräten mit beenden.
    this.authService.logout({ revokeServerSession: false });
  }
}
